// Admin Create Lesson Plan: turns each child's recorded progress (from
// /api/lesson-plans/children-progress) into a proposed plan row.
//
// One-on-One courses use the Learning Zone's catalogs. Their Syllabus Tracker
// session number is the scene/song/story id (that is how the Learning Zone
// practice panel files uploads), so "last scored session 4" means "Scene 4".
import {
  ACTION_SONGS,
  BALGOPAL_STORIES,
  BALGRUHA_STORIES,
  SAINT_STORIES,
  SCENES,
  SIDEBAR_TREE,
} from '../../data/learningZoneMockData';

// Below this share of the maximum score the last item is revised first.
export const REVISION_BELOW = 0.5;
export const TBD_LABEL = 'To Be Decided (Select from syllabus)';
export const DEFAULT_FOCUS = 'scenes';
export const DEFAULT_COMPUTER_APP = 'gcompris';

// Sidebar view -> One-on-One course. `topic` is the Syllabus Tracker topic
// (null: not tracked yet, so every child starts at the first item).
export const ONE_ON_ONE_COURSES = {
  scenes: { label: 'Scenes', topic: 'Scenes', icon: 'scene', items: SCENES },
  'action-songs': { label: 'Action Songs', topic: 'Action Songs', icon: 'song', items: ACTION_SONGS },
  'action-songs-vocal': {
    label: 'Vocal Songs',
    topic: 'Action Songs',
    icon: 'song',
    items: ACTION_SONGS.filter((song) => song.type === 'vocal'),
  },
  'action-songs-instrumental': {
    label: 'Instrumental Songs',
    topic: 'Action Songs',
    icon: 'song',
    items: ACTION_SONGS.filter((song) => song.type === 'instrumental'),
  },
  'stories-balgopal': { label: 'Balgopal Stories', topic: null, icon: 'story', items: BALGOPAL_STORIES },
  'stories-saint': { label: 'Saint Stories', topic: 'Saint Story', icon: 'story', items: SAINT_STORIES },
  'stories-balgruha': {
    label: '2 min Balgruha Stories',
    topic: '2 Min Balgruha Stories',
    icon: 'story',
    items: BALGRUHA_STORIES,
  },
};

// Order of the One-on-One dropdown groups (the focused course comes first).
const ONE_ON_ONE_ORDER = [
  'scenes',
  'action-songs',
  'stories-balgopal',
  'stories-saint',
  'stories-balgruha',
];

export const COMPUTER_APPS = [
  { id: 'gcompris', label: 'GCompris' },
  { id: 'typing-master', label: 'Typing Master' },
  { id: 'artweaver', label: 'Art Weaver' },
  { id: 'canva', label: 'Canva (Drawing)' },
  { id: 'toonytool', label: 'Toonytool' },
];
const COMPUTER_APP_IDS = new Set(COMPUTER_APPS.map((app) => app.id));

// The sidebar: the student Learning Zone's course tree, limited to the
// courses a visit can plan (English, Computer Apps, Life Skills, Stories).
// Every row selects a focus here instead of navigating to a student page.
const PLANNED_COURSES = ['english', 'computer-apps', 'life-skills', 'stories'];
// Computer Apps > Artweaver also lists the art lessons (Art Therapy's rows until 2026-10-26), which
// visits don't plan, so here it stays the computer app it was.
const planningItem = (item) => (item.id === 'artweaver' ? { id: item.id, label: item.label, icon: item.icon } : item);
const toPlanningRow = (original) => {
  const item = planningItem(original);
  const { to, ...row } = item;
  return {
    ...row,
    view: item.view || item.id,
    ...(item.children
      ? { children: item.children.filter((child) => !child.to).map(toPlanningRow) }
      : {}),
  };
};
export const LESSON_PLAN_COURSE_TREE = SIDEBAR_TREE.filter((item) => PLANNED_COURSES.includes(item.id)).map(
  toPlanningRow
);

export const isComputerFocus = (view) => COMPUTER_APP_IDS.has(view);
export const isOneOnOneFocus = (view) => Boolean(ONE_ON_ONE_COURSES[view]);

const itemTitle = (item) => item.title;

// Option key: course view + item id + kind (keeps dropdown values unique).
const optionKey = (courseView, itemId, kind) => `${courseView}:${itemId}:${kind}`;

const optionFor = (courseView, item, kind, note = '') => {
  const course = ONE_ON_ONE_COURSES[courseView];
  const suffix = { next: 'Next', revision: 'Revision', review: 'Review practice', start: 'Start' }[kind];
  return {
    key: optionKey(courseView, item.id, kind),
    label: suffix ? `${itemTitle(item)} (${suffix})` : itemTitle(item),
    topic: course.topic || course.label,
    session: item.id,
    kind,
    note,
  };
};

export const TBD_OPTION = { key: 'tbd', label: TBD_LABEL, topic: '', session: null, kind: 'tbd', note: '' };

/**
 * The proposed One-on-One item for a child in a course:
 *   - last scored item below REVISION_BELOW of the max -> revise it
 *   - otherwise the item after the last scored one (Next)
 *   - practice uploaded but not scored yet -> review that practice
 *   - nothing recorded -> the course's first item (Start)
 *   - everything done -> To Be Decided
 */
export const proposeOneOnOne = (child, courseView, maxScore = 30) => {
  const course = ONE_ON_ONE_COURSES[courseView];
  if (!course || course.items.length === 0) return TBD_OPTION;
  const progress = course.topic ? child?.syllabus?.[course.topic] : null;
  const indexOf = (session) => course.items.findIndex((item) => item.id === session);
  const lastIndex = progress?.lastSession ? indexOf(progress.lastSession) : -1;

  if (lastIndex >= 0) {
    const last = course.items[lastIndex];
    const { lastScore, lastComment } = progress;
    if (lastScore !== null && lastScore !== undefined && lastScore < maxScore * REVISION_BELOW) {
      return optionFor(
        courseView,
        last,
        'revision',
        lastComment || `Revise ${itemTitle(last)} before moving ahead (last score ${lastScore}/${maxScore}).`
      );
    }
    const next = course.items[lastIndex + 1];
    if (!next) {
      return { ...TBD_OPTION, note: `All ${course.label} completed. Choose the next learning.` };
    }
    return optionFor(
      courseView,
      next,
      'next',
      lastComment ? `${lastComment} Move on to ${itemTitle(next)}.` : `${itemTitle(last)} completed. Assign next learning.`
    );
  }

  const submittedIndex = progress?.lastSubmittedSession ? indexOf(progress.lastSubmittedSession) : -1;
  if (submittedIndex >= 0) {
    const practised = course.items[submittedIndex];
    return optionFor(
      courseView,
      practised,
      'review',
      `Practice uploaded for ${itemTitle(practised)}; review it with the child.`
    );
  }

  return optionFor(courseView, course.items[0], 'start', `No recorded progress yet. Start with ${itemTitle(course.items[0])}.`);
};

// The proposed computer activity. Only GCompris records progress.
export const proposeComputer = (child, appId = DEFAULT_COMPUTER_APP) => {
  const app = COMPUTER_APPS.find((item) => item.id === appId) || COMPUTER_APPS[0];
  if (app.id !== 'gcompris') return { key: app.id, label: app.label, app: app.id };
  const game = child?.gcompris;
  if (!game) return { key: 'gcompris', label: 'GCompris (Start)', app: 'gcompris' };
  const level = Number(game.level) || 1;
  const label =
    game.passed || game.completed
      ? `GCompris – ${game.activityTitle} (Next level ${level + 1})`
      : `GCompris – ${game.activityTitle} (Practice level ${level})`;
  return { key: 'gcompris', label, app: 'gcompris' };
};

export const NO_COMPUTER = { key: 'none', label: 'None', app: '' };

// The option a row's value stands for: same key, or (for values loaded from a
// saved plan) the same label.
export const findOption = (options, current) =>
  options.find((option) => option.key === current?.key) ||
  options.find((option) => option.label === current?.label) ||
  null;

// Dropdown options for a child's Computer cell.
export const computerOptions = (child, current) => {
  const options = [...COMPUTER_APPS.map((app) => proposeComputer(child, app.id)), NO_COMPUTER];
  return current && !findOption(options, current) ? [current, ...options] : options;
};

// Dropdown groups for a child's One-on-One cell: the focused course first,
// each with the child's proposal and then every item of the course.
export const oneOnOneOptionGroups = (child, focusView, maxScore, current) => {
  const focusIsOneOnOne = isOneOnOneFocus(focusView);
  const views = focusIsOneOnOne
    ? [focusView, ...ONE_ON_ONE_ORDER.filter((view) => view !== focusView && !view.startsWith(`${focusView}-`))]
    : ONE_ON_ONE_ORDER;
  const groups = views.map((view) => {
    const course = ONE_ON_ONE_COURSES[view];
    const proposal = proposeOneOnOne(child, view, maxScore);
    const items = course.items.map((item) => optionFor(view, item, 'plain'));
    return {
      label: course.label,
      options: proposal.kind === 'tbd' ? items : [proposal, ...items],
    };
  });
  const known = Boolean(findOption(groups.flatMap((group) => group.options), current));
  const extra = [TBD_OPTION, ...(current && !known && current.key !== 'tbd' ? [current] : [])];
  return [...groups, { label: 'Other', options: extra }];
};

// A fresh proposed row for a child.
export const proposeRow = (child, { focusView = DEFAULT_FOCUS, computerApp = DEFAULT_COMPUTER_APP, maxScore = 30 } = {}) => {
  const oneOnOneView = isOneOnOneFocus(focusView) ? focusView : DEFAULT_FOCUS;
  const oneOnOne = proposeOneOnOne(child, oneOnOneView, maxScore);
  return {
    studentId: child._id,
    name: child.name,
    computer: proposeComputer(child, computerApp),
    oneOnOne,
    specialInstructions: oneOnOne.note,
    edited: { computer: false, oneOnOne: false, specialInstructions: false },
  };
};

// Re-propose a row for a new focus, keeping whatever the admin changed by hand.
export const refocusRow = (row, child, options) => {
  const fresh = proposeRow(child, options);
  return {
    ...row,
    computer: row.edited.computer ? row.computer : fresh.computer,
    oneOnOne: row.edited.oneOnOne ? row.oneOnOne : fresh.oneOnOne,
    specialInstructions: row.edited.specialInstructions || row.edited.oneOnOne ? row.specialInstructions : fresh.specialInstructions,
  };
};

// A saved plan item back into a row (labels as saved; marked edited so a new
// focus does not overwrite them).
export const rowFromSaved = (item, child) => ({
  studentId: String(item.studentId?._id || item.studentId),
  name: child?.name || item.studentId?.name || 'Child',
  computer: item.computer ? { key: `saved:${item.computer}`, label: item.computer, app: item.computerApp || '' } : NO_COMPUTER,
  oneOnOne: item.oneOnOne
    ? {
        key: `saved:${item.oneOnOne}`,
        label: item.oneOnOne,
        topic: item.oneOnOneTopic || '',
        session: item.oneOnOneSession || null,
        kind: 'saved',
        note: '',
      }
    : TBD_OPTION,
  specialInstructions: item.specialInstructions || '',
  edited: { computer: true, oneOnOne: true, specialInstructions: true },
});

// A row as the API stores it.
export const rowToItem = (row) => ({
  studentId: row.studentId,
  computer: row.computer?.key === NO_COMPUTER.key ? '' : row.computer?.label || '',
  computerApp: row.computer?.app || '',
  oneOnOne: row.oneOnOne?.kind === 'tbd' ? '' : row.oneOnOne?.label || '',
  oneOnOneTopic: row.oneOnOne?.kind === 'tbd' ? '' : row.oneOnOne?.topic || '',
  oneOnOneSession: row.oneOnOne?.kind === 'tbd' ? null : row.oneOnOne?.session || null,
  specialInstructions: row.specialInstructions || '',
});
